import type { CompanyMetadataDto } from "~/ui-rtk/api/types/company.types"
import {
  isRegionalCountry,
  normalizeGaPropertyId,
  type RegionalCountry,
} from "~/ui-rtk/constants/regional"

export type RegionalMetadata = NonNullable<CompanyMetadataDto["regional"]>

export interface RegionalFormValues {
  countryBreakoutEnabled?: boolean
  usaCampaignSlug?: string
  canadaCampaignSlug?: string
  /** GA4 property id → country; owned by CountryBreakoutGaMap. */
  gaPropertyCountries?: Record<string, RegionalCountry>
}

/**
 * The `metadata.regional` object pipeline-dbt reads (comp_meta__config:
 * `$.regional.enabled` / `usa_slug` / `canada_slug` / `ga_property_countries`).
 * Blank slugs are omitted rather than written as "" — dbt treats a present key
 * as configured — and property ids are normalized to the bare numeric form the
 * dbt join expects. The whole object is replaced on save (the API merges
 * metadata one level deep), so every key is rebuilt from the form here.
 */
export function buildRegionalMetadata(
  values: RegionalFormValues,
): RegionalMetadata {
  const usaSlug = values.usaCampaignSlug?.trim()
  const canadaSlug = values.canadaCampaignSlug?.trim()
  const gaPropertyCountries: Record<string, RegionalCountry> = {}
  for (const [propertyId, country] of Object.entries(
    values.gaPropertyCountries ?? {},
  )) {
    const id = normalizeGaPropertyId(propertyId)
    if (id && isRegionalCountry(country)) {
      gaPropertyCountries[id] = country
    }
  }
  return {
    enabled: Boolean(values.countryBreakoutEnabled),
    ...(usaSlug ? { usa_slug: usaSlug } : {}),
    ...(canadaSlug ? { canada_slug: canadaSlug } : {}),
    ga_property_countries: gaPropertyCountries,
  }
}

/** Form values for the stored `metadata.regional` (missing → defaults). */
export function regionalFormValues(
  regional: RegionalMetadata | undefined,
): Required<RegionalFormValues> {
  return {
    countryBreakoutEnabled: Boolean(regional?.enabled),
    usaCampaignSlug: regional?.usa_slug ?? "",
    canadaCampaignSlug: regional?.canada_slug ?? "",
    gaPropertyCountries: regional?.ga_property_countries ?? {},
  }
}
